import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Phone, MapPin, UserPlus, Trash2, Save, Edit2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api, { formatApiError } from '../api';
import { CLIENT_TYPES, INTERVENTION_STATUS, formatDate } from '../constants';

const AdminClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const fetchClient = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      setClient(data);
      setForm(data);
    } catch (e) {
      toast.error('Client introuvable');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  const save = async () => {
    try {
      const payload = {
        name: form.name, type: form.type, phone: form.phone, email: form.email,
        address: form.address, city: form.city, postalCode: form.postalCode,
        comment: form.comment, status: form.status,
      };
      await api.patch(`/clients/${clientId}`, payload);
      toast.success('Client mis à jour');
      setEditing(false);
      fetchClient();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendAccess = async () => {
    try {
      const { data } = await api.post(`/clients/${clientId}/access`);
      toast.success(data.emailSent ? 'Accès envoyé par email' : 'Compte créé (email non envoyé)');
      fetchClient();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement ce client ?')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client supprimé');
      navigate('/admin/clients');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading || !client) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>;
  }

  return (
    <div data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour aux clients
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-neutral-900">Fiche client</h2>
              {!editing && (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="edit-client-button">
                  <Edit2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            {editing ? (
              <div className="space-y-3">
                <div><Label>Nom</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CLIENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Téléphone</Label><Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Adresse</Label><Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Ville</Label><Input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>CP</Label><Input value={form.postalCode || ''} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></div>
                </div>
                <div><Label>Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="actif">Actif</SelectItem><SelectItem value="inactif">Inactif</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Commentaire</Label><Textarea value={form.comment || ''} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={2} /></div>
                <div className="flex gap-2">
                  <Button onClick={save} className="flex-1 bg-red-700 hover:bg-red-600" data-testid="save-client-button"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
                  <Button variant="outline" onClick={() => { setEditing(false); setForm(client); }}>Annuler</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <p className="text-lg font-bold text-neutral-900">{client.name}</p>
                <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">{CLIENT_TYPES[client.type]}</span>
                <span className={`text-xs px-2 py-0.5 rounded ml-1 ${client.status === 'actif' ? 'bg-green-100 text-green-700' : 'bg-neutral-200 text-neutral-500'}`}>{client.status}</span>
                <div className="pt-3 space-y-1.5 text-neutral-600">
                  {client.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {client.phone}</p>}
                  {client.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {client.email}</p>}
                  {(client.address || client.city) && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {client.address} {client.postalCode} {client.city}</p>}
                  {client.comment && <p className="text-neutral-500 italic pt-2">{client.comment}</p>}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-3">
            <p className="text-sm text-neutral-600">
              Accès espace client : {client.hasAccess
                ? <span className="text-green-600 font-medium">Actif</span>
                : <span className="text-neutral-400">Non créé</span>}
            </p>
            <Button variant="outline" className="w-full" onClick={sendAccess} disabled={!client.email} data-testid="detail-send-access">
              <UserPlus className="w-4 h-4 mr-2" /> {client.hasAccess ? 'Renvoyer l\'accès' : 'Créer / envoyer l\'accès'}
            </Button>
            <Button variant="ghost" className="w-full text-red-600 hover:text-red-700" onClick={remove} data-testid="delete-client-button">
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer le client
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h2 className="font-bold text-neutral-900 mb-4">Historique des interventions ({client.interventions?.length || 0})</h2>
            {(!client.interventions || client.interventions.length === 0) ? (
              <p className="text-neutral-400 text-sm py-8 text-center">Aucune intervention pour ce client.</p>
            ) : (
              <div className="space-y-2">
                {client.interventions.map((it) => {
                  const st = INTERVENTION_STATUS[it.status] || {};
                  return (
                    <Link key={it.id} to={`/admin/interventions/${it.id}`} data-testid={`client-intervention-${it.number}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 border border-neutral-100">
                      <div>
                        <p className="font-medium text-sm text-neutral-900">{it.number}</p>
                        <p className="text-xs text-neutral-500">{formatDate(it.createdAt)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClientDetail;
